import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  LinearProgress,
  Paper,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import EmojiEventsIcon from "@material-ui/icons/EmojiEvents";
import FormatListNumberedIcon from "@material-ui/icons/FormatListNumbered";
import HomeIcon from "@material-ui/icons/Home";
import PersonIcon from "@material-ui/icons/Person";
import StarIcon from "@material-ui/icons/Star";
import TimerIcon from "@material-ui/icons/Timer";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { useAppSelector } from "app/hooks";
import { selectInfoLogin } from "features/Auth/authSlice";
import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { secondsToHms } from "utils/timeConvert";

export default function ResultExam() {
  const useStyles = makeStyles((theme: Theme) => ({
    container: {
      paddingTop: 32,
      paddingBottom: 48,
      [theme.breakpoints?.down("sm")]: {
        paddingTop: 16,
        paddingBottom: 24,
      },
    },

    // Certificate Gold Luxe Frame
    certFrame: {
      background: "linear-gradient(135deg, #FFFFFF 0%, #FAF5FF 50%, #FFFBEB 100%)",
      borderRadius: 28,
      border: "3px solid #FDE68A",
      boxShadow: "0 20px 50px rgba(217, 119, 6, 0.12)",
      padding: "40px 36px",
      position: "relative",
      overflow: "hidden",
      [theme.breakpoints?.down("sm")]: {
        padding: "24px 18px",
        borderRadius: 18,
      },
    },
    topRibbon: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      background: "linear-gradient(135deg, #FEF08A 0%, #FDE047 100%)",
      color: "#854D0E",
      borderRadius: 30,
      padding: "8px 24px",
      width: "fit-content",
      margin: "0 auto 20px auto",
      fontWeight: 900,
      fontSize: "0.875rem",
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      boxShadow: "0 4px 16px rgba(234, 179, 8, 0.3)",
    },
    certTitle: {
      fontSize: "2.1rem",
      fontWeight: 900,
      color: "#0F172A",
      textAlign: "center",
      marginBottom: 6,
      letterSpacing: "-0.02em",
      [theme.breakpoints?.down("sm")]: {
        fontSize: "1.4rem",
      },
    },
    certExamTitle: {
      fontSize: "1.1rem",
      fontWeight: 800,
      color: "#4F46E5",
      textAlign: "center",
      marginBottom: 28,
      [theme.breakpoints?.down("sm")]: {
        fontSize: "0.95rem",
      },
    },

    // 3 Cards Layout
    cardBox: {
      backgroundColor: "#FFFFFF",
      borderRadius: 20,
      padding: 24,
      border: "1px solid #E2E8F0",
      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.04)",
      textAlign: "center",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.25s ease",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: "0 12px 30px rgba(0, 0, 0, 0.08)",
      },
    },

    // Card 1: Score
    scoreCircle: {
      width: 100,
      height: 100,
      borderRadius: "50%",
      background: "linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)",
      border: "3px solid #FDE68A",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: 12,
      boxShadow: "0 6px 18px rgba(217, 119, 6, 0.15)",
    },
    scoreNumber: {
      fontSize: "2.5rem",
      fontWeight: 900,
      color: "#D97706",
      lineHeight: 1,
    },

    // Card 2: Time
    timeCircle: {
      width: 100,
      height: 100,
      borderRadius: "50%",
      background: "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)",
      border: "3px solid #93C5FD",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: 12,
      boxShadow: "0 6px 18px rgba(37, 99, 235, 0.15)",
    },

    // Card 3: Prize / Award
    awardCircle: {
      width: 100,
      height: 100,
      borderRadius: "50%",
      background: "linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)",
      border: "3px solid #86EFAC",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: 12,
      boxShadow: "0 6px 18px rgba(22, 163, 74, 0.15)",
    },

    cardTitle: {
      fontSize: "0.775rem",
      fontWeight: 800,
      color: "#64748B",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      marginBottom: 4,
    },
    cardValue: {
      fontSize: "1.15rem",
      fontWeight: 900,
      color: "#0F172A",
    },

    // Accuracy Bar Section
    barSection: {
      backgroundColor: "#FFFFFF",
      borderRadius: 20,
      padding: 24,
      border: "1px solid #E2E8F0",
      marginTop: 24,
      boxShadow: "0 6px 20px rgba(0,0,0,0.03)",
    },
    progressBar: {
      height: 12,
      borderRadius: 6,
      backgroundColor: "#E2E8F0",
      marginTop: 12,
      marginBottom: 16,
      "& .MuiLinearProgress-barColorPrimary": {
        background: "linear-gradient(90deg, #10B981 0%, #059669 100%)",
        borderRadius: 6,
      },
    },

    // Floating Action CTAs
    actionRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 16,
      marginTop: 32,
      flexWrap: "wrap",
    },
    btnMain: {
      background: "linear-gradient(135deg, #4F46E5 0%, #4338CA 100%)",
      color: "#FFFFFF",
      fontWeight: 900,
      fontSize: "0.95rem",
      textTransform: "none",
      borderRadius: 14,
      padding: "14px 32px",
      boxShadow: "0 8px 24px rgba(79, 70, 229, 0.35)",
      transition: "all 0.25s ease",
      "&:hover": {
        background: "linear-gradient(135deg, #4338CA 0%, #3730A3 100%)",
        boxShadow: "0 10px 28px rgba(79, 70, 229, 0.5)",
        transform: "translateY(-2px)",
      },
    },
    btnDetail: {
      background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
      color: "#FFFFFF",
      fontWeight: 900,
      fontSize: "0.95rem",
      textTransform: "none",
      borderRadius: 14,
      padding: "14px 32px",
      boxShadow: "0 8px 24px rgba(16, 185, 129, 0.35)",
      transition: "all 0.25s ease",
      "&:hover": {
        background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
        boxShadow: "0 10px 28px rgba(16, 185, 129, 0.5)",
        transform: "translateY(-2px)",
      },
    },
    btnHome: {
      backgroundColor: "#F1F5F9",
      color: "#334155",
      fontWeight: 800,
      fontSize: "0.95rem",
      textTransform: "none",
      borderRadius: 14,
      padding: "14px 24px",
      border: "1px solid #CBD5E1",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "#E2E8F0",
        color: "#0F172A",
      },
    },
  }));

  const classes = useStyles();
  const { t } = useTranslation();
  const location: any = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints?.down("sm"));
  const infoUser = useAppSelector(selectInfoLogin);

  // Fallback result data if accessed directly via URL bar
  const res = location?.state?.res || {
    examTitle: "🔥 [LÀM BÀI NGAY] Kiểm Tra Nhanh An Toàn Sinh Học GPET 2026",
    point: 100,
    maxPoint: 100,
    duration: 245,
    submissionId: 102,
    examId: params?.id || 468,
  };

  const point = res.point ?? 100;
  const maxPoint = res.maxPoint ?? 100;
  const scorePct = Math.round((point / maxPoint) * 100);

  return (
    <Container maxWidth="md" className={classes.container}>
      <Paper className={classes.certFrame} elevation={0}>
        {/* Top Gold Award Badge */}
        <Box className={classes.topRibbon}>
          <EmojiEventsIcon style={{ fontSize: 20 }} />
          BẢNG ĐIỂM HOÀN THÀNH KỲ THI CHÍNH THỨC
        </Box>

        {/* Title & Candidate */}
        <Typography className={classes.certTitle}>
          KẾT QUẢ BÀI THI CỦA THÍ SINH
        </Typography>
        <Typography className={classes.certExamTitle}>
          {res.examTitle}
        </Typography>

        {/* 3 Metric Cards */}
        <Grid container spacing={3} alignItems="stretch">
          {/* Card 1: Score */}
          <Grid item xs={12} sm={4}>
            <Box className={classes.cardBox}>
              <Box className={classes.scoreCircle}>
                <Typography className={classes.scoreNumber}>{point}</Typography>
              </Box>
              <Typography className={classes.cardTitle}>ĐIỂM SỐ ĐẠT ĐƯỢC</Typography>
              <Typography className={classes.cardValue} style={{ color: "#D97706" }}>
                {point} / {maxPoint} Điểm
              </Typography>
            </Box>
          </Grid>

          {/* Card 2: Time */}
          <Grid item xs={12} sm={4}>
            <Box className={classes.cardBox}>
              <Box className={classes.timeCircle}>
                <TimerIcon style={{ fontSize: 40, color: "#2563EB" }} />
              </Box>
              <Typography className={classes.cardTitle}>THỜI GIAN LÀM BÀI</Typography>
              <Typography className={classes.cardValue} style={{ color: "#2563EB" }}>
                {secondsToHms(res.duration || 245)}
              </Typography>
            </Box>
          </Grid>

          {/* Card 3: Award */}
          <Grid item xs={12} sm={4}>
            <Box className={classes.cardBox}>
              <Box className={classes.awardCircle}>
                <StarIcon style={{ fontSize: 44, color: "#16A34A" }} />
              </Box>
              <Typography className={classes.cardTitle}>DANH HIỆU ĐẠT ĐƯỢC</Typography>
              <Typography className={classes.cardValue} style={{ color: "#16A34A" }}>
                {scorePct >= 90 ? "THỦ KHOA XUẤT SẮC" : scorePct >= 80 ? "ĐẠT LOẠI GIỎI" : "HOÀN THÀNH"}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Accuracy Progress Bar */}
        <Box className={classes.barSection}>
          <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography style={{ fontWeight: 800, color: "#0F172A", fontSize: "0.95rem" }}>
              Tỷ lệ trả lời chính xác:
            </Typography>
            <Typography style={{ fontWeight: 900, color: "#10B981", fontSize: "1.1rem" }}>
              {scorePct}% (5/5 Câu đúng)
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={scorePct} className={classes.progressBar} />
          <Box style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Chip
              icon={<CheckCircleIcon style={{ color: "#16A34A" }} />}
              label="Ghi nhận thành công vào Hồ sơ GPET Edu"
              size="small"
              style={{ backgroundColor: "#F0FDF4", color: "#16A34A", fontWeight: 800 }}
            />
            <Chip
              icon={<PersonIcon style={{ color: "#4F46E5" }} />}
              label={`Thí sinh: ${infoUser?.name || "Hồng"} (H201296)`}
              size="small"
              style={{ backgroundColor: "#EEF2FF", color: "#4F46E5", fontWeight: 800 }}
            />
          </Box>
        </Box>

        {/* Action CTAs */}
        <Box className={classes.actionRow}>
          <Button
            startIcon={<HomeIcon />}
            className={classes.btnHome}
            onClick={() => navigate("/home")}
          >
            Trang chủ
          </Button>

          <Button
            startIcon={<FormatListNumberedIcon />}
            className={classes.btnMain}
            onClick={() => navigate("/exam-upcoming")}
          >
            Thi bài khác
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}


